import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import api from '../../libs/axios';

// DECISION-007 B3: a tournament's no-ball and wide runs. New matches of the tournament
// take them when they are created; "Apply to matches not yet started" also changes the
// tournament's matches that have not begun (live and finished matches are never touched).
// Saved on its own, separately from the tournament form above.

interface Props {
  tournamentId: number | string;
  // Values from the tournament record, used until the preview answers.
  initialWideRuns?: number | null;
  initialNoBallRuns?: number | null;
}

interface Preview {
  wide_runs: number | null;
  no_ball_runs: number | null;
  upcoming: number | null;
  matches: { id: number; title: string | null; status: string | null }[];
}

const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || fallback;
const numOrNull = (v: any): number | null => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

// GET /tournaments/:id/match-rules/preview, parsed defensively (DECISION-007 section 4).
const parsePreview = (d: any): Preview => {
  const t = d?.tournament ?? d?.current ?? d ?? {};
  const list: any[] = d?.matches ?? d?.upcoming_matches_list ?? d?.affected_matches ?? [];
  const count = numOrNull(d?.upcoming_count ?? d?.upcoming_matches ?? d?.matches_not_started ?? d?.count ?? d?.affected ?? (Array.isArray(list) ? list.length : null));
  return {
    wide_runs: numOrNull(t?.wide_runs),
    no_ball_runs: numOrNull(t?.no_ball_runs),
    upcoming: count,
    matches: (Array.isArray(list) ? list : []).slice(0, 50).map((m: any) => ({
      id: Number(m?.id ?? m?.match_id),
      title: m?.title ?? m?.name ?? null,
      status: m?.status ?? null,
    })).filter((m) => Number.isInteger(m.id)),
  };
};

const TournamentMatchRules: React.FC<Props> = ({ tournamentId, initialWideRuns, initialNoBallRuns }) => {
  const [wide, setWide] = useState<string>(initialWideRuns != null ? String(initialWideRuns) : '');
  const [noBall, setNoBall] = useState<string>(initialNoBallRuns != null ? String(initialNoBallRuns) : '');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [apply, setApply] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const loadPreview = useCallback(async (w: string, n: string, keepInputs: boolean) => {
    setPreviewError(null);
    try {
      const q = new URLSearchParams();
      if (w) q.set('wide_runs', w);
      if (n) q.set('no_ball_runs', n);
      const qs = q.toString();
      const res: any = await api.get(`/tournaments/${tournamentId}/match-rules/preview${qs ? `?${qs}` : ''}`);
      const p = parsePreview(res.data);
      setPreview(p);
      if (!keepInputs) {
        setWide(p.wide_runs != null ? String(p.wide_runs) : '');
        setNoBall(p.no_ball_runs != null ? String(p.no_ball_runs) : '');
      }
    } catch (e: any) {
      setPreview(null);
      setPreviewError(errorMessage(e, 'Could not load the tournament\'s match rules.'));
    }
  }, [tournamentId]);

  useEffect(() => { if (tournamentId) loadPreview('', '', false); }, [tournamentId, loadPreview]);

  const rangeError = (v: string) => {
    if (!v) return null;
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 && n <= 10 ? null : 'Whole number from 1 to 10';
  };
  const wideErr = rangeError(wide);
  const noBallErr = rangeError(noBall);
  const reasonOk = !apply || reason.trim().length >= 5;
  const canSave = !saving && !wideErr && !noBallErr && reasonOk && touched;
  const nonStandard = (wide !== '' && Number(wide) !== 1) || (noBall !== '' && Number(noBall) !== 1);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setResult(null);
    try {
      const res: any = await api.put(`/tournaments/${tournamentId}/match-rules`, {
        wide_runs: wide ? Number(wide) : null,
        no_ball_runs: noBall ? Number(noBall) : null,
        apply_to_upcoming: apply,
        reason: reason.trim() || undefined,
      });
      const d = res?.data ?? {};
      const updated = numOrNull(d?.updated ?? d?.matches_updated ?? d?.updated_matches ?? d?.applied);
      const msg = res?.message || 'Match rules saved';
      setResult(apply && updated != null ? `${msg}. ${updated} match${updated === 1 ? '' : 'es'} not yet started now use these rules.` : msg);
      toast.success(msg);
      setTouched(false);
      setApply(false);
      setReason('');
      loadPreview('', '', false);
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to save the match rules'), { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  // Typing a value refreshes the count of matches that would change, when applying.
  const onWide = (v: string) => {
    const clean = v.replace(/[^0-9]/g, '');
    setWide(clean); setTouched(true); setResult(null);
    if (apply) loadPreview(clean, noBall, true);
  };
  const onNoBall = (v: string) => {
    const clean = v.replace(/[^0-9]/g, '');
    setNoBall(clean); setTouched(true); setResult(null);
    if (apply) loadPreview(wide, clean, true);
  };

  return (
    <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Match rules</h3>
        <p className="text-sm text-gray-500">
          No-ball and wide runs for this tournament&apos;s matches. New matches take them when they are created. Saved separately
          from the form above. Empty = not set (the match&apos;s own setting or the format default applies).
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">No-ball runs</label>
          <input type="number" min={1} max={10} value={noBall} placeholder="Not set" disabled={saving}
            onChange={(e) => onNoBall(e.target.value)}
            className={`w-full rounded-lg border px-3 py-2 text-sm ${noBallErr ? 'border-red-400' : 'border-gray-300'}`} />
          {noBallErr && <div className="text-xs text-red-600 mt-1">{noBallErr}</div>}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Wide runs</label>
          <input type="number" min={1} max={10} value={wide} placeholder="Not set" disabled={saving}
            onChange={(e) => onWide(e.target.value)}
            className={`w-full rounded-lg border px-3 py-2 text-sm ${wideErr ? 'border-red-400' : 'border-gray-300'}`} />
          {wideErr && <div className="text-xs text-red-600 mt-1">{wideErr}</div>}
        </div>
      </div>

      {nonStandard && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 max-w-2xl">
          <FiAlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Runs other than 1 can only be scored on app 1.0.25 or later. Phones on older versions are refused on these matches with an update message.</span>
        </div>
      )}

      <label className="flex items-start gap-2 text-sm text-gray-800 max-w-2xl">
        <input type="checkbox" className="mt-1" checked={apply} disabled={saving}
          onChange={(e) => { setApply(e.target.checked); setTouched(true); if (e.target.checked) loadPreview(wide, noBall, true); }} />
        <span>
          Also apply to this tournament&apos;s matches not yet started
          {preview?.upcoming != null ? ` (${preview.upcoming} match${preview.upcoming === 1 ? '' : 'es'})` : ''}.
          Live and finished matches are never changed.
        </span>
      </label>

      {apply && preview && preview.matches.length > 0 && (
        <div className="text-xs text-gray-500 max-w-2xl">
          {preview.matches.map((m) => `#${m.id}${m.title ? ` ${m.title}` : ''}`).join(' · ')}
          {preview.upcoming != null && preview.upcoming > preview.matches.length ? ' · …' : ''}
        </div>
      )}
      {previewError && <p className="text-xs text-red-600">{previewError}</p>}

      {apply && (
        <div className="max-w-2xl">
          <label className="block text-sm font-semibold text-gray-900 mb-1" htmlFor="tournament-rules-reason">
            Reason <span className="font-normal text-gray-500">(kept in the audit log)</span>
          </label>
          <textarea id="tournament-rules-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={2} disabled={saving}
            placeholder="e.g. Organiser confirmed 2-run no-balls for the whole 100-ball league"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          {!reasonOk && reason.length > 0 && <p className="text-xs text-gray-500 mt-1">At least 5 characters.</p>}
        </div>
      )}

      {result && (
        <div className="flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-900 max-w-2xl">
          <FiCheckCircle className="w-4 h-4 mt-0.5" />
          <span>{result}</span>
        </div>
      )}

      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={!canSave}
          className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium">
          {saving ? 'Saving…' : 'Save match rules'}
        </button>
      </div>
    </div>
  );
};

export default TournamentMatchRules;
